import { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { appsApi, type App } from "../api/apps";

export function AppsPage() {
  const [apps, setApps] = useState<App[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", ownerEmail: "" });

  async function refresh() {
    setLoading(true);
    const res = await appsApi.list();
    setApps(res.apps);
    setLoading(false);
  }

  useEffect(() => {
    refresh();
  }, []);

  async function submit() {
    await appsApi.create(form);
    setOpen(false);
    setForm({ name: "", description: "", ownerEmail: "" });
    await refresh();
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Apps</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>
          New app
        </Button>
      </Stack>

      {loading ? (
        <Typography color="text.secondary">Loading…</Typography>
      ) : apps.length === 0 ? (
        <Typography color="text.secondary">No apps yet. Create one to issue API keys.</Typography>
      ) : (
        <Grid container spacing={2}>
          {apps.map((app) => (
            <Grid key={app.appId} item xs={12} sm={6} md={4}>
              <Card
                component={RouterLink}
                to={`/apps/${app.appId}`}
                sx={{ textDecoration: "none", display: "block" }}
              >
                <CardContent>
                  <Typography variant="h6">{app.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {app.appId}
                  </Typography>
                  {app.description && (
                    <Typography variant="body2" mt={1}>
                      {app.description}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                    Owner: {app.ownerEmail}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>New app</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              autoFocus
              fullWidth
            />
            <TextField
              label="Description"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              fullWidth
              multiline
              rows={2}
            />
            <TextField
              label="Owner email"
              value={form.ownerEmail}
              onChange={(e) => setForm((f) => ({ ...f, ownerEmail: e.target.value }))}
              fullWidth
              type="email"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submit}
            disabled={!form.name || !form.ownerEmail}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
